import { ArrowDownUp, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { useAccounts } from '../features/accounts/queries';
import { useAnalyticsSymbolLots } from '../features/analytics/queries';
import type { AnalyticsSymbolLotDto } from '../lib/api/endpoints';
import { formatCurrency } from '../lib/format/currency';
import { positionReplayHref } from '../lib/navigation/positionReplay';

type LotFilter = 'ALL' | 'WINS' | 'LOSSES';
type LotSortKey =
  | 'lot'
  | 'openedAt'
  | 'closedAt'
  | 'quantity'
  | 'entryPrice'
  | 'exitPrice'
  | 'realizedPnl'
  | 'holdingDays';

function dateLabel(value: string): string {
  return value.slice(0, 10);
}

function numberValue(lot: AnalyticsSymbolLotDto, key: LotSortKey, index: number): number {
  if (key === 'lot') return index + 1;
  if (key === 'openedAt' || key === 'closedAt') return new Date(lot[key]).getTime();
  return Number(lot[key]);
}

function summary(lots: AnalyticsSymbolLotDto[]) {
  const totalPnl = lots.reduce((sum, lot) => sum + Number(lot.realizedPnl), 0);
  const wins = lots.filter((lot) => lot.win);
  const holds = lots.reduce((sum, lot) => sum + lot.holdingDays, 0);
  const pnlValues = lots.map((lot) => Number(lot.realizedPnl));
  return {
    totalLots: lots.length,
    totalPnl,
    winRate: lots.length === 0 ? 0 : (wins.length / lots.length) * 100,
    avgHold: lots.length === 0 ? 0 : holds / lots.length,
    largestWin: pnlValues.length === 0 ? 0 : Math.max(...pnlValues),
    largestLoss: pnlValues.length === 0 ? 0 : Math.min(...pnlValues),
  };
}

export function SymbolLots() {
  const { symbol = '' } = useParams();
  const [searchParams] = useSearchParams();
  const accounts = useAccounts();
  const accountId = searchParams.get('accountId') ?? accounts.data?.[0]?.id ?? null;
  const account = accounts.data?.find((item) => item.id === accountId) ?? accounts.data?.[0];
  const currency = account?.currency ?? 'USD';
  const lots = useAnalyticsSymbolLots(accountId, symbol);
  const [filter, setFilter] = useState<LotFilter>('ALL');
  const [sortKey, setSortKey] = useState<LotSortKey>('closedAt');
  const [descending, setDescending] = useState(true);
  const filteredLots = useMemo(() => {
    const selected = (lots.data ?? []).filter((lot) => {
      if (filter === 'WINS') return lot.win;
      if (filter === 'LOSSES') return !lot.win;
      return true;
    });
    return selected
      .map((lot, index) => ({ lot, originalIndex: index }))
      .sort((left, right) => {
        const direction = descending ? -1 : 1;
        const delta =
          numberValue(left.lot, sortKey, left.originalIndex) -
          numberValue(right.lot, sortKey, right.originalIndex);
        return delta === 0 ? left.lot.id.localeCompare(right.lot.id) : delta * direction;
      });
  }, [descending, filter, lots.data, sortKey]);
  const totals = summary(filteredLots.map((item) => item.lot));

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <nav className="text-sm text-text-muted" aria-label="Breadcrumb">
          <Link className="text-primary hover:text-primary-hover" to="/analytics">
            Analytics
          </Link>
          <span> / {symbol.toUpperCase()} lots</span>
        </nav>
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{symbol.toUpperCase()} closed lots</h1>
            <p className="mt-1 text-sm text-text-muted">
              {account?.name ?? 'Account'} FIFO history
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            {accountId ? (
              <Button asChild size="sm" variant="secondary">
                <Link
                  aria-label={`Replay trades for ${symbol.toUpperCase()}`}
                  to={positionReplayHref(symbol, accountId)}
                >
                  <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                  Trade replay
                </Link>
              </Button>
            ) : null}
            <div
              className="inline-flex self-start rounded border border-border"
              aria-label="Lot filter"
            >
              {(['ALL', 'WINS', 'LOSSES'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                  className="px-3 py-2 text-sm text-text-muted aria-pressed:bg-surface-raised aria-pressed:text-text"
                >
                  {value === 'ALL' ? 'All' : value === 'WINS' ? 'Wins' : 'Losses'}
                </button>
              ))}
            </div>
          </div>
        </header>

        <section className="rounded border border-border bg-surface p-4">
          {lots.isLoading ? (
            <p className="text-sm text-text-muted">Loading lots...</p>
          ) : lots.isError ? (
            <p className="text-sm text-danger">Could not load lots.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-text-muted">
                    <tr>
                      <SortableHeader
                        active={sortKey === 'lot'}
                        label="Lot #"
                        onClick={() => toggleSort('lot')}
                      />
                      <SortableHeader
                        active={sortKey === 'openedAt'}
                        label="Opened"
                        onClick={() => toggleSort('openedAt')}
                      />
                      <SortableHeader
                        active={sortKey === 'closedAt'}
                        label="Closed"
                        onClick={() => toggleSort('closedAt')}
                      />
                      <SortableHeader
                        active={sortKey === 'quantity'}
                        label="Qty"
                        onClick={() => toggleSort('quantity')}
                      />
                      <SortableHeader
                        active={sortKey === 'entryPrice'}
                        label="Entry"
                        onClick={() => toggleSort('entryPrice')}
                      />
                      <SortableHeader
                        active={sortKey === 'exitPrice'}
                        label="Exit"
                        onClick={() => toggleSort('exitPrice')}
                      />
                      <SortableHeader
                        active={sortKey === 'realizedPnl'}
                        label="P&L"
                        onClick={() => toggleSort('realizedPnl')}
                      />
                      <SortableHeader
                        active={sortKey === 'holdingDays'}
                        label="Hold"
                        onClick={() => toggleSort('holdingDays')}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLots.map(({ lot, originalIndex }) => (
                      <tr key={lot.id} className="border-t border-border hover:bg-surface-raised">
                        <td className="px-3 py-3 tabular-nums">{originalIndex + 1}</td>
                        <td className="px-3 py-3 tabular-nums">{dateLabel(lot.openedAt)}</td>
                        <td className="px-3 py-3 tabular-nums">{dateLabel(lot.closedAt)}</td>
                        <td className="px-3 py-3 tabular-nums">{lot.quantity}</td>
                        <td className="px-3 py-3 tabular-nums">
                          {formatCurrency(lot.entryPrice, currency)}
                        </td>
                        <td className="px-3 py-3 tabular-nums">
                          {formatCurrency(lot.exitPrice, currency)}
                        </td>
                        <td
                          className={
                            Number(lot.realizedPnl) >= 0
                              ? 'px-3 py-3 tabular-nums text-success'
                              : 'px-3 py-3 tabular-nums text-danger'
                          }
                        >
                          {formatCurrency(lot.realizedPnl, currency)}
                        </td>
                        <td className="px-3 py-3 tabular-nums">{lot.holdingDays}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredLots.length === 0 ? (
                  <p className="py-4 text-sm text-text-muted">No closed lots match this filter.</p>
                ) : null}
              </div>
              <dl className="mt-4 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-3 xl:grid-cols-6">
                <Summary label="Lots" value={totals.totalLots.toString()} />
                <Summary label="Total P&L" value={formatCurrency(totals.totalPnl, currency)} />
                <Summary label="Win Rate" value={`${totals.winRate.toFixed(1)}%`} />
                <Summary label="Avg Hold" value={`${totals.avgHold.toFixed(1)}d`} />
                <Summary label="Largest Win" value={formatCurrency(totals.largestWin, currency)} />
                <Summary
                  label="Largest Loss"
                  value={formatCurrency(totals.largestLoss, currency)}
                />
              </dl>
            </>
          )}
        </section>
      </div>
    </main>
  );

  function toggleSort(nextKey: LotSortKey): void {
    if (sortKey === nextKey) {
      setDescending((value) => !value);
      return;
    }
    setSortKey(nextKey);
    setDescending(true);
  }
}

function SortableHeader({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <th className="px-3 py-2 font-medium">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text"
      >
        <span>{label}</span>
        <ArrowDownUp className={`h-3.5 w-3.5 ${active ? 'text-text' : ''}`} aria-hidden="true" />
      </button>
    </th>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-text-muted">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
